import { Popover, PopoverAnchor, PopoverContent } from '@heartplus/ui';
import * as React from 'react';

import {
  RAW_JSON_VIEWER_CLASS_NAME,
  RawJsonViewer,
} from '../../../fhir-resource-viewer/raw-json-viewer';
import type { FhirResourceViewerResource } from '../../../fhir-resource-viewer/types';
import type { TokenInlineProps } from './config';

type TokenDebugPopoverProps = {
  props: TokenInlineProps;
  children: React.ReactNode;
};

type TokenDebugResourcesContextValue = {
  resources: FhirResourceViewerResource[];
  requestResources: () => void;
  isEnabled?: boolean;
  isLoading?: boolean;
  error?: Error | null;
};

type AutoFillDebugTrace = {
  tokenId?: string;
  resourceType?: string;
  resourceId?: string;
  resourcePath?: string;
  resourceSource?: string;
  result?: unknown;
};

type ResourceEvidence =
  | {
      kind: 'used';
      resource: FhirResourceViewerResource;
      reason: string;
    }
  | {
      kind: 'candidate';
      resource: FhirResourceViewerResource;
      reason: string;
      score: number;
    };

type ParsedJsonLike =
  | { kind: 'empty' }
  | { kind: 'json'; value: unknown }
  | { kind: 'text'; value: string };

const TokenDebugResourcesContext =
  React.createContext<TokenDebugResourcesContextValue | null>(null);

export function TokenDebugResourcesProvider({
  value,
  children,
}: Readonly<{
  value: TokenDebugResourcesContextValue;
  children: React.ReactNode;
}>) {
  return (
    <TokenDebugResourcesContext.Provider value={value}>
      {children}
    </TokenDebugResourcesContext.Provider>
  );
}

function isLocalhost(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
}

function parseJsonLike(value: string | undefined): ParsedJsonLike {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) {
    return { kind: 'empty' };
  }

  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (typeof parsed === 'string') {
      return parseJsonLike(parsed);
    }
    return { kind: 'json', value: parsed };
  } catch {
    return { kind: 'text', value: trimmed };
  }
}

function DebugRow({
  label,
  value,
}: Readonly<{ label: string; value: React.ReactNode }>) {
  return (
    <div className="grid grid-cols-[7rem_minmax(0,1fr)] gap-2">
      <dt className="text-gray-500">{label}</dt>
      <dd className="min-w-0 break-words text-gray-900">{value}</dd>
    </div>
  );
}

function BackendMapping({ value }: Readonly<{ value?: string }>) {
  const parsed = parseJsonLike(value);

  if (parsed.kind === 'empty') {
    return <pre className={RAW_JSON_VIEWER_CLASS_NAME}>Not configured</pre>;
  }

  if (parsed.kind === 'json') {
    return <RawJsonViewer value={parsed.value} />;
  }

  return <pre className={RAW_JSON_VIEWER_CLASS_NAME}>{parsed.value}</pre>;
}

function parseAutoFillDebug(
  value: string | undefined,
): AutoFillDebugTrace | undefined {
  const parsed = parseJsonLike(value);
  if (parsed.kind !== 'json' || !isRecord(parsed.value)) {
    return undefined;
  }

  return {
    tokenId: readString(parsed.value['tokenId']),
    resourceType: readString(parsed.value['resourceType']),
    resourceId: readString(parsed.value['resourceId']),
    resourcePath: readString(parsed.value['resourcePath']),
    resourceSource: readString(parsed.value['resourceSource']),
    result: parsed.value['result'],
  };
}

function collectSearchTerms(props: TokenInlineProps): string[] {
  const terms = new Set<string>();
  const addTerm = (value: string | undefined) => {
    const normalized = value?.trim();
    if (normalized && normalized.length >= 2) {
      terms.add(normalized.toLowerCase());
    }
  };

  const mapping = props.backendMapping ?? '';
  const literalRegex = /'([^']+)'|"([^"]+)"/g;
  for (const match of mapping.matchAll(literalRegex)) {
    addTerm(match[1] ?? match[2]);
  }

  // Also pick up common numeric FHIR/IEEE codes in expression text.
  for (const match of mapping.matchAll(/\b\d{3,}\b/g)) {
    addTerm(match[0]);
  }

  addTerm(props.value);
  return Array.from(terms);
}

function collectResourcePrimitives(resource: unknown): string[] {
  const values: string[] = [];
  const visit = (value: unknown) => {
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }

    if (isRecord(value)) {
      Object.values(value).forEach(visit);
      return;
    }

    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      values.push(String(value).toLowerCase());
    }
  };

  visit(resource);
  return values;
}

function findResourceEvidence(
  props: TokenInlineProps,
  resources: FhirResourceViewerResource[],
): ResourceEvidence[] {
  const trace = parseAutoFillDebug(props.autoFillDebug);
  if (trace) {
    const exact = resources.find((resource) => {
      const idMatches = trace.resourceId && resource.id === trace.resourceId;
      const pathMatches =
        trace.resourcePath && resource.path === trace.resourcePath;
      const sourceMatches =
        trace.resourceSource && resource.source === trace.resourceSource;
      const typeMatches =
        !trace.resourceType || resource.resourceType === trace.resourceType;

      return typeMatches && (idMatches || pathMatches || sourceMatches);
    });

    if (exact) {
      return [
        {
          kind: 'used',
          resource: exact,
          reason:
            trace.resourcePath ??
            trace.resourceId ??
            trace.resourceSource ??
            'backend autofill trace',
        },
      ];
    }
  }

  const terms = collectSearchTerms(props);
  const scopedType = props.resourceType?.trim();
  const candidates = resources
    .map((resource): ResourceEvidence | undefined => {
      const typeMatches =
        !scopedType ||
        scopedType === 'Bundle' ||
        resource.resourceType === scopedType;

      if (!typeMatches) {
        return undefined;
      }

      let score = scopedType && resource.resourceType === scopedType ? 2 : 0;
      const primitiveValues = collectResourcePrimitives(resource.resource);
      const matchedTerms = terms.filter((term) =>
        primitiveValues.some((value) => value.includes(term)),
      );

      score += matchedTerms.length * 3;

      if (props.value) {
        const normalizedValue = props.value.toLowerCase().trim();
        if (
          normalizedValue &&
          primitiveValues.some((value) => value.includes(normalizedValue))
        ) {
          score += 2;
        }
      }

      if (score <= 0) {
        return undefined;
      }

      return {
        kind: 'candidate',
        resource,
        score,
        reason:
          matchedTerms.length > 0
            ? `matched ${matchedTerms.slice(0, 3).join(', ')}`
            : `resource_type ${resource.resourceType}`,
      };
    })
    .filter((item): item is ResourceEvidence => item !== undefined)
    .sort((left, right) => {
      if (left.kind === 'candidate' && right.kind === 'candidate') {
        return right.score - left.score;
      }
      return 0;
    });

  return candidates.slice(0, 3);
}

function ResourceCard({ evidence }: Readonly<{ evidence: ResourceEvidence }>) {
  const { resource } = evidence;
  const title =
    resource.display?.title ||
    (resource.id
      ? `${resource.resourceType}/${resource.id}`
      : resource.resourceType);

  return (
    <details className="rounded-md border border-gray-200 bg-white">
      <summary className="cursor-pointer list-none px-3 py-2">
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-2">
            <span className="min-w-0 truncate font-medium text-gray-900">
              {title}
            </span>
            <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase text-gray-600">
              {resource.source ?? resource.resourceType}
            </span>
          </div>
          <p className="text-[11px] text-gray-500">{evidence.reason}</p>
          {resource.display?.chips?.length ? (
            <div className="flex flex-wrap gap-1">
              {resource.display.chips.slice(0, 3).map((chip, index) => (
                <span
                  key={`${chip.kind}-${chip.label}-${index}`}
                  className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] text-blue-700"
                >
                  {chip.label}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </summary>
      <div className="border-t border-gray-100 p-2">
        <div className="mb-1 flex justify-end">
          <button
            type="button"
            className="rounded bg-gray-100 px-2 py-0.5 text-[10px] text-gray-700 hover:bg-gray-200"
            onClick={() => {
              void navigator.clipboard.writeText(
                JSON.stringify(resource.resource, null, 2),
              );
            }}
          >
            Copy JSON
          </button>
        </div>
        <RawJsonViewer value={resource.resource} />
      </div>
    </details>
  );
}

function ResourceEvidenceSection({
  props,
}: Readonly<{ props: TokenInlineProps }>) {
  const resourcesContext = React.useContext(TokenDebugResourcesContext);
  const resources = resourcesContext?.resources ?? [];
  const evidence = findResourceEvidence(props, resources);
  const hasUsedResource = evidence.some((item) => item.kind === 'used');
  const title = hasUsedResource ? 'Used resource' : 'Candidate resources';

  return (
    <div className="space-y-2">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        FHIR resources used
      </p>
      {resourcesContext?.isLoading ? (
        <p className="text-gray-500">Loading FHIR resources...</p>
      ) : resourcesContext?.error ? (
        <p className="text-red-600">
          Failed to load FHIR resources: {resourcesContext.error.message}
        </p>
      ) : evidence.length > 0 ? (
        <div className="space-y-2">
          <p className="text-[11px] font-medium text-gray-600">{title}</p>
          {evidence.map((item) => (
            <ResourceCard
              key={`${item.kind}-${item.resource.resourceType}-${item.resource.id ?? item.resource.path ?? item.reason}`}
              evidence={item}
            />
          ))}
        </div>
      ) : (
        <p className="text-gray-500">
          No matching FHIR resource found for this mapping.
        </p>
      )}
    </div>
  );
}

export function TokenDebugPopover({
  props,
  children,
}: Readonly<TokenDebugPopoverProps>) {
  const [isOpen, setIsOpen] = React.useState(false);
  const resourcesContext = React.useContext(TokenDebugResourcesContext);
  const closeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  React.useEffect(
    () => () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    },
    [],
  );

  // Opt-in dev tooling: the debug popover renders only when an enclosing
  // TokenDebugResourcesProvider explicitly enables it (the Critical Summary
  // "Token debug" toggle). Without a provider — e.g. read-mode notes in the
  // dashboard interpretation popover — resourcesContext is null, so it stays
  // hidden no matter what, instead of leaking in on localhost.
  if (!isLocalhost() || resourcesContext?.isEnabled !== true) {
    return <>{children}</>;
  }

  const open = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setIsOpen(true);
    resourcesContext?.requestResources();
  };
  const close = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = setTimeout(() => setIsOpen(false), 120);
  };
  const autoFillStatus = props.autoFillStatus === true ? 'Enabled' : 'Disabled';

  return (
    <Popover
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          close();
        }
      }}
    >
      <PopoverAnchor asChild>
        <span
          className="inline-flex"
          contentEditable={false}
          onPointerEnter={open}
          onPointerLeave={close}
          onFocusCapture={open}
          onBlurCapture={close}
          data-token-debug-trigger="true"
        >
          {children}
        </span>
      </PopoverAnchor>
      <PopoverContent
        side="right"
        align="center"
        sideOffset={6}
        avoidCollisions
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
        onPointerEnter={open}
        onPointerLeave={close}
        className="w-[30rem] max-h-[80vh] max-w-[calc(100vw-2rem)] overflow-y-auto p-3"
        data-testid="token-debug-popover"
      >
        <div className="space-y-3 text-xs">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Token debug
            </p>
            <p className="mt-1 font-mono text-xs text-gray-900">
              {props.tokenName || props.tokenId || 'Unknown token'}
            </p>
          </div>
          <dl className="space-y-1">
            <DebugRow label="token_id" value={props.tokenId || '-'} />
            <DebugRow label="token_type" value={props.tokenType || '-'} />
            <DebugRow
              label="resource_type"
              value={props.resourceType || 'Not configured'}
            />
            <DebugRow label="auto_fill" value={autoFillStatus} />
          </dl>
          <div className="space-y-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              backend_mapping
            </p>
            <BackendMapping value={props.backendMapping} />
          </div>
          <ResourceEvidenceSection props={props} />
        </div>
      </PopoverContent>
    </Popover>
  );
}

export const tokenDebugPopoverInternals = {
  parseJsonLike,
  findResourceEvidence,
  isLocalhost,
};

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
